import { SquareChartGantt } from "lucide-react";
import React from "react";
import { useDispatch } from "react-redux";

import { IgButton, IgLink, IgTooltip } from "@intelligems/ig-theme";
import { StripeSubscriptionStatus } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useGetBillingPortal } from "@src/api/stripe/get-billing-portal";
import { Tag } from "@src/components/BillingPanel/components/Products/components/PriceBox/Tag";
import { BalanceExceededBadge } from "@src/features/admin/components/Icons/BalanceExceededBadge";
import { BillingAutoScaleIcon } from "@src/features/admin/components/Icons/BillingAutoScaleIcon";
import { openDrawer } from "@src/store/slices/drawers";

import { LegacyBadge } from "./Icons/LegacyBadge";
import { StatusBadge } from "./Icons/StatusBadge";
import { CurrencyFormat, DecimalFormat, openStripe } from "./utils";

interface SubscriptionDetailsHeaderProps {
  viewCharges: {
    subscriptionLinkId: string;
    organizationId: string;
  } | null;
  stripeSubscriptionId: string | null | undefined;
  isBalanceExceeded: boolean;
  autoScale: boolean;
  status: StripeSubscriptionStatus;
  stripeSubscriptionScheduleId: string | null | undefined;
  v1?: boolean;
  customer?: {
    name?: string | null | undefined;
    stripeCustomerId: string;
    billing?: boolean | null;
  };
  cancelAt?: number | null | undefined;
  agencyId?: string;
  orderCount?: number | null;
  coupon?: {
    percentOff?: number | undefined;
    amountOff?: number | undefined;
  };
}

export const SubscriptionDetailsHeader = ({
  status,
  viewCharges,
  isBalanceExceeded,
  stripeSubscriptionId,
  stripeSubscriptionScheduleId,
  autoScale,
  v1,
  customer,
  agencyId,
  cancelAt,
  orderCount,
  coupon,
}: SubscriptionDetailsHeaderProps) => {
  const getBillingPortal = useGetBillingPortal(false);
  const dispatch = useDispatch();

  return (
    <Grid
      container
      item
      justifyContent={"space-between"}
      alignItems={"center"}
      flexWrap={"nowrap"}
    >
      <Grid container item columnSpacing={2} alignItems={"center"}>
        {v1 ? null : (
          <Grid item>
            <StatusBadge status={status} cancelAt={cancelAt} />
          </Grid>
        )}
        <Grid item>
          {v1 ? (
            <LegacyBadge />
          ) : (
            <IgLink
              // color={"inherit"}
              onClick={() =>
                openStripe(`subscriptions/${stripeSubscriptionId}`)
              }
            >
              {stripeSubscriptionId}
            </IgLink>
          )}
        </Grid>

        {orderCount ? (
          <Grid item>{DecimalFormat.format(orderCount)} orders / month</Grid>
        ) : null}
        {coupon ? (
          <Grid
            item
            sx={{
              "& .MuiBox-root": {
                fontWeight: `500 !important`,
                fontSize: "14px !important",
              },
            }}
          >
            <Tag variant={"success"}>
              {coupon.amountOff
                ? CurrencyFormat.format(coupon.amountOff / 100)
                : `${coupon.percentOff}%`}{" "}
              Off Applied
            </Tag>
          </Grid>
        ) : null}
      </Grid>
      <Grid
        container
        item
        columnSpacing={2}
        alignItems={"center"}
        justifyContent={"end"}
      >
        {isBalanceExceeded ? (
          <Grid item>
            <BalanceExceededBadge />
          </Grid>
        ) : null}
        <Grid item>
          <BillingAutoScaleIcon autoScale={autoScale} />
        </Grid>
        {stripeSubscriptionScheduleId ? (
          <Grid item sx={{ marginTop: "7px" }}>
            <IgTooltip title={"Subscription has phases"}>
              <SquareChartGantt size={18} />
            </IgTooltip>
          </Grid>
        ) : null}
        {v1 || !viewCharges ? null : (
          <Grid item>
            <IgLink
              onClick={() =>
                dispatch(
                  openDrawer({
                    type: "meterLog",
                    subscription: {
                      subscriptionLinkId: viewCharges.subscriptionLinkId,
                      organizationId: viewCharges.organizationId,
                      agencyId,
                    },
                  })
                )
              }
            >
              View Usage
            </IgLink>
          </Grid>
        )}
        {customer ? (
          <Grid item>
            <IgLink
              onClick={() =>
                openStripe(`customers/${customer.stripeCustomerId}`)
              }
            >
              {customer.name}
            </IgLink>
          </Grid>
        ) : null}
        {customer && !customer.billing ? (
          <Grid item>
            <IgButton
              color={"error"}
              onClick={() => {
                getBillingPortal.mutate({
                  pathParams: undefined,
                  queryParams: undefined,
                  body: {
                    stripeCustomerId: customer?.stripeCustomerId,
                    stripeSubscriptionId: undefined,
                  },
                });
              }}
            >
              Create Payment Link
            </IgButton>
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );
};
